import type { AdCount } from '../../../api/types/adviewApiTypes';
import type { TLastSearch } from '../../../components/PageWrappers/SharedComponents/LastSearchCard/types';

import { MotorSearch } from '../../../components/HomePage/components/MotorSearch/MotorSearch';
import { MotorSearch as NewMotorSearch } from '../../../components/HomePage/features/MotorSearch/MotorSearch';
import { VerticalSearch } from '../../../components/HomePage/components/VerticalSearch/VerticalSearch';
import { VERTICAL } from '../../../helpers/verticals';

import { ISearchPageFilter } from '../../../api/types/searchPageApiTypes';

import {
  Wrapper,
  Container,
} from '../../../components/HomePage/Layouts/HomePageSearchManager.styled';
import { FEATURE_CARS_KEYWORD_SEARCH } from '../../../utils';

export type HomePageSearchManagerProps = {
  domain: string;
  vertical: VERTICAL;
  setLastSearch: (searchTerms: string, url: string) => void;
  lastSearchData?: TLastSearch;
  lastSearchDataTrackingValue?: string;
  className?: string;
  adCount: AdCount;
  motorSearchFilters: ISearchPageFilter[];
};

const HomePageSearchManager = (props: HomePageSearchManagerProps) => {
  const {
    domain,
    vertical,
    className,
    lastSearchData,
    setLastSearch,
    lastSearchDataTrackingValue,
    adCount,
    motorSearchFilters,
  } = props;

  const displayVertical = (vertical: VERTICAL) => {
    switch (vertical) {
      case VERTICAL.MOTOR:
        return FEATURE_CARS_KEYWORD_SEARCH ? (
          <NewMotorSearch
            makes={extractMakeOptions(motorSearchFilters)}
            priceOptions={extractPriceOptions(motorSearchFilters)}
            yearOptions={extractYearOptions(motorSearchFilters)}
            domain={domain}
            setLastSearch={setLastSearch}
          />
        ) : (
          <MotorSearch
            domain={domain}
            adCount={adCount}
            searchFilters={motorSearchFilters}
            setLastSearch={setLastSearch}
          />
        );
      case VERTICAL.MARKETPLACE:
      case VERTICAL.FARMING:
        return (
          <VerticalSearch
            baseUrl={domain}
            setLastSearch={setLastSearch}
            lastSearchData={lastSearchData}
            lastSearchDataTrackingValue={lastSearchDataTrackingValue}
            vertical={vertical}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Wrapper className={className}>
      <Container>{displayVertical(vertical)}</Container>
    </Wrapper>
  );
};

type FilterOption = {
  displayName: string;
  value: string;
  count?: string;
};

type MappedOption = {
  label: string;
  value: string | null;
  count: string | null;
};

function extractFilterOptions(
  motorSearchFilters: ISearchPageFilter[],
  filterName: 'price' | 'year' | 'makeModel',
  property: 'from' | 'to' | 'all' | 'popular',
): MappedOption[] {
  const filter = motorSearchFilters.find(
    (filter) => filter.name === filterName,
  );
  const options = filter?.subFilters?.[0]?.values?.[0]?.[property] as
    | FilterOption[]
    | undefined;

  return (
    options?.map((option) => {
      return {
        label: option.displayName,
        value: option.value === '' ? null : option.value,
        count: option.count ?? null,
      };
    }) ?? []
  );
}

function extractPriceOptions(motorSearchFilters: ISearchPageFilter[]) {
  const priceOptionsFrom = extractFilterOptions(
    motorSearchFilters,
    'price',
    'from',
  );
  const priceOptionsTo = extractFilterOptions(
    motorSearchFilters,
    'price',
    'to',
  );

  return {
    min: priceOptionsFrom,
    max: priceOptionsTo,
  };
}

function extractYearOptions(motorSearchFilters: ISearchPageFilter[]) {
  const yearOptionsFrom = extractFilterOptions(
    motorSearchFilters,
    'year',
    'from',
  );
  const yearOptionsTo = extractFilterOptions(motorSearchFilters, 'year', 'to');

  return {
    min: yearOptionsFrom,
    max: yearOptionsTo,
  };
}

function extractMakeOptions(motorSearchFilters: ISearchPageFilter[]) {
  const allMakesOptions = extractFilterOptions(
    motorSearchFilters,
    'makeModel',
    'all',
  );
  const popularMakesOptions = extractFilterOptions(
    motorSearchFilters,
    'makeModel',
    'popular',
  );

  return {
    popular: popularMakesOptions,
    all: allMakesOptions,
  };
}

export { HomePageSearchManager };
